<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  useFunctionGetter,
  useMapGetter,
  useStore,
} from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';
import integrationAPI from 'dashboard/api/integrations';
import Spinner from 'shared/components/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const APP_ID = 'conversation_parser';
const PROVIDERS = [
  'openai',
  'anthropic',
  'gemini',
  'deepseek',
  'mistral',
  'openrouter',
  'openai_compatible',
];
const MODEL_SUGGESTIONS = {
  openai: ['gpt-4o-mini', 'gpt-4o', 'gpt-4.1-mini', 'gpt-4.1', 'o4-mini'],
  anthropic: ['claude-haiku-4-5', 'claude-sonnet-4-6'],
  gemini: ['gemini-2.5-flash', 'gemini-2.5-pro', 'gemini-2.0-flash'],
  deepseek: ['deepseek-chat', 'deepseek-reasoner'],
  mistral: ['mistral-small-latest', 'mistral-large-latest'],
  openrouter: [
    'moonshotai/kimi-k2',
    'deepseek/deepseek-chat',
    'anthropic/claude-sonnet-4.6',
  ],
  openai_compatible: ['kimi-k2-0905-preview', 'moonshot-v1-8k'],
};
const STANDARD_CONTACT_KEYS = [
  'name',
  'email',
  'phone_number',
  'identifier',
  'city',
  'country',
  'company_name',
];

const store = useStore();
const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const integrationLoaded = ref(false);
const selectedFields = ref({});
const activityNoteEnabled = ref(false);
const apiKey = ref('');
const apiKeyPresent = ref(false);
const model = ref('');
const provider = ref('openai');
const apiBase = ref('');
const allInboxes = ref(true);
const selectedInboxes = ref({});
const usage = ref({});
const isSaving = ref(false);
const disconnectDialogRef = ref(null);

const integration = useFunctionGetter('integrations/getIntegration', APP_ID);
const uiFlags = useMapGetter('integrations/getUIFlags');
const contactAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'contact_attribute'
);
const conversationAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);
const inboxes = useMapGetter('inboxes/getInboxes');

const hook = computed(() => integration.value?.hooks?.[0]);
const isConnected = computed(() => Boolean(hook.value));

const fieldKey = (fieldModel, key) => `${fieldModel}.${key}`;

const contactStandardOptions = STANDARD_CONTACT_KEYS.map(key => ({
  model: 'contact',
  key,
  standard: true,
  label: t(
    `INTEGRATION_SETTINGS.CONVERSATION_PARSER.FIELDS.STANDARD.${key.toUpperCase()}`
  ),
}));

const contactCustomOptions = computed(() =>
  (contactAttributes.value || []).map(attribute => ({
    model: 'contact',
    key: attribute.attribute_key,
    standard: false,
    label: attribute.attribute_display_name,
  }))
);

const conversationCustomOptions = computed(() =>
  (conversationAttributes.value || []).map(attribute => ({
    model: 'conversation',
    key: attribute.attribute_key,
    standard: false,
    label: attribute.attribute_display_name,
  }))
);

const fieldGroups = computed(() => [
  {
    id: 'contact_standard',
    label: t(
      'INTEGRATION_SETTINGS.CONVERSATION_PARSER.FIELDS.CONTACT_STANDARD'
    ),
    options: contactStandardOptions,
  },
  {
    id: 'contact_custom',
    label: t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.FIELDS.CONTACT_CUSTOM'),
    options: contactCustomOptions.value,
  },
  {
    id: 'conversation_custom',
    label: t(
      'INTEGRATION_SETTINGS.CONVERSATION_PARSER.FIELDS.CONVERSATION_CUSTOM'
    ),
    options: conversationCustomOptions.value,
  },
]);

const syncFromHook = () => {
  const settings = hook.value?.settings || {};
  const selection = {};
  (settings.fields || []).forEach(field => {
    selection[fieldKey(field.model, field.key)] = true;
  });
  selectedFields.value = selection;
  activityNoteEnabled.value = Boolean(settings.activity_note_enabled);
  // api_key is write-only: never read back from the server, only set/rotated here.
  apiKey.value = '';
  apiKeyPresent.value = Boolean(settings.api_key_present);
  model.value = settings.model || '';
  provider.value = settings.provider || 'openai';
  apiBase.value = settings.api_base || '';
  const inboxIds = settings.inbox_ids || [];
  allInboxes.value = !inboxIds.length;
  selectedInboxes.value = Object.fromEntries(inboxIds.map(id => [id, true]));
};

watch(hook, syncFromHook, { immediate: true });

const allOptions = computed(() =>
  fieldGroups.value.flatMap(group => group.options)
);

const buildSettings = () => {
  const settings = {
    fields: allOptions.value
      .filter(option => selectedFields.value[fieldKey(option.model, option.key)])
      .map(option => ({
        model: option.model,
        key: option.key,
        standard: option.standard,
      })),
    activity_note_enabled: activityNoteEnabled.value,
    model: model.value.trim(),
    provider: provider.value,
    api_base: apiBase.value.trim(),
    inbox_ids: allInboxes.value
      ? []
      : (inboxes.value || [])
          .filter(inbox => selectedInboxes.value[inbox.id])
          .map(inbox => inbox.id),
  };
  // Only send the key when the admin typed one; blank keeps the stored key server-side.
  const key = apiKey.value.trim();
  if (key) settings.api_key = key;
  return settings;
};

const inboxOptions = computed(() =>
  (inboxes.value || []).map(inbox => ({
    id: inbox.id,
    name: inbox.name,
    channelType: (inbox.channel_type || '').replace('Channel::', ''),
  }))
);

const modelSuggestions = computed(
  () => MODEL_SUGGESTIONS[provider.value] || []
);
const showApiBase = computed(() =>
  ['openai', 'openai_compatible'].includes(provider.value)
);

const fetchUsage = async () => {
  try {
    const { data } = await integrationAPI.getConversationParserUsage();
    usage.value = data.usage || {};
  } catch (error) {
    usage.value = {};
  }
};

const usageRows = computed(() =>
  Object.entries(usage.value)
    .sort((a, b) => b[0].localeCompare(a[0]))
    .map(([month, entry]) => ({
      month,
      calls: entry.calls || 0,
      inputTokens: entry.input_tokens || 0,
      outputTokens: entry.output_tokens || 0,
      totalTokens: (entry.input_tokens || 0) + (entry.output_tokens || 0),
      model: entry.model || '',
    }))
);

const connect = async () => {
  try {
    await store.dispatch('integrations/createHook', {
      app_id: APP_ID,
      settings: { fields: [] },
    });
    useAlert(t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.CONNECT.SUCCESS'));
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.CONNECT.ERROR'));
  }
};

const saveSettings = async () => {
  isSaving.value = true;
  try {
    await store.dispatch('integrations/updateHook', {
      hookId: hook.value.id,
      settings: buildSettings(),
    });
    useAlert(t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.SAVE.SUCCESS'));
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.SAVE.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const openDisconnectDialog = () => disconnectDialogRef.value?.open();

const disconnect = async () => {
  try {
    await store.dispatch('integrations/deleteHook', {
      appId: APP_ID,
      hookId: hook.value.id,
    });
    useAlert(t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.DISCONNECT.SUCCESS'));
  } catch (error) {
    useAlert(t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.DISCONNECT.ERROR'));
  }
};

onMounted(async () => {
  await Promise.all([
    store.dispatch('integrations/get', APP_ID),
    store.dispatch('attributes/get'),
    store.dispatch('inboxes/get'),
    fetchUsage(),
  ]);
  integrationLoaded.value = true;
});
</script>

<template>
  <div class="flex-grow flex-shrink p-4 overflow-auto max-w-6xl mx-auto">
    <div v-if="integrationLoaded" class="flex flex-col gap-6">
      <div
        class="flex flex-col items-start justify-between lg:flex-row lg:items-center p-6 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow gap-6"
      >
        <div
          class="flex items-start lg:items-center justify-start flex-1 m-0 gap-6 flex-col lg:flex-row"
        >
          <div class="flex h-16 w-16 items-center justify-center flex-shrink-0">
            <img
              :src="`/dashboard/images/integrations/${APP_ID}.png`"
              class="max-w-full rounded-md border border-n-weak shadow-sm block dark:hidden bg-n-alpha-3"
            />
            <img
              :src="`/dashboard/images/integrations/${APP_ID}-dark.png`"
              class="max-w-full rounded-md border border-n-weak shadow-sm hidden dark:block bg-n-alpha-2"
            />
          </div>
          <div>
            <h3 class="mb-1 text-xl font-medium text-n-slate-12">
              {{ integration.name }}
            </h3>
            <p class="text-n-slate-11 text-sm leading-6">
              {{ replaceInstallationName(integration.description) }}
            </p>
          </div>
        </div>
        <div class="flex justify-center items-center mb-0">
          <Button
            v-if="!isConnected"
            faded
            blue
            :is-loading="uiFlags.isCreatingHook"
            :label="
              t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.CONNECT.BUTTON')
            "
            @click="connect"
          />
          <Button
            v-else
            faded
            ruby
            :label="
              t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.DISCONNECT.BUTTON')
            "
            @click="openDisconnectDialog"
          />
        </div>
      </div>

      <div
        v-if="isConnected"
        class="flex flex-col gap-6 p-6 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
      >
        <div>
          <h4 class="text-lg font-medium text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.FIELDS.HEADER') }}
          </h4>
          <p class="text-n-slate-11 text-sm leading-6">
            {{
              t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.FIELDS.DESCRIPTION')
            }}
          </p>
        </div>

        <div
          v-for="group in fieldGroups"
          :key="group.id"
          class="flex flex-col gap-3"
        >
          <h5 class="text-sm font-medium text-n-slate-12">
            {{ group.label }}
          </h5>
          <p v-if="!group.options.length" class="text-n-slate-11 text-sm">
            {{ t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.FIELDS.EMPTY') }}
          </p>
          <div
            v-else
            class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2"
          >
            <label
              v-for="option in group.options"
              :key="fieldKey(option.model, option.key)"
              class="flex items-center gap-2 cursor-pointer text-sm text-n-slate-12"
            >
              <Checkbox
                v-model="selectedFields[fieldKey(option.model, option.key)]"
              />
              {{ option.label }}
            </label>
          </div>
        </div>

        <div class="flex flex-col gap-3 border-t border-n-weak pt-6">
          <div>
            <h5 class="text-sm font-medium text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.INBOXES.HEADER') }}
            </h5>
            <p class="text-xs text-n-slate-11">
              {{
                t(
                  'INTEGRATION_SETTINGS.CONVERSATION_PARSER.INBOXES.DESCRIPTION'
                )
              }}
            </p>
          </div>
          <div class="flex items-center gap-3">
            <Switch v-model="allInboxes" />
            <p class="text-sm text-n-slate-12 mb-0">
              {{ t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.INBOXES.ALL') }}
            </p>
          </div>
          <div v-if="!allInboxes">
            <p v-if="!inboxOptions.length" class="text-n-slate-11 text-sm">
              {{ t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.INBOXES.EMPTY') }}
            </p>
            <div
              v-else
              class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2"
            >
              <label
                v-for="inbox in inboxOptions"
                :key="inbox.id"
                class="flex items-center gap-2 cursor-pointer text-sm text-n-slate-12"
              >
                <Checkbox v-model="selectedInboxes[inbox.id]" />
                {{ inbox.name }}
                <span class="text-xs text-n-slate-11">
                  {{ inbox.channelType }}
                </span>
              </label>
            </div>
          </div>
        </div>

        <div class="flex items-center gap-3">
          <Switch v-model="activityNoteEnabled" />
          <div>
            <p class="text-sm text-n-slate-12 mb-0">
              {{
                t(
                  'INTEGRATION_SETTINGS.CONVERSATION_PARSER.ACTIVITY_NOTE.LABEL'
                )
              }}
            </p>
            <p class="text-xs text-n-slate-11 mb-0">
              {{
                t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.ACTIVITY_NOTE.HELP')
              }}
            </p>
          </div>
        </div>

        <div class="flex flex-col gap-3 border-t border-n-weak pt-6">
          <div>
            <h5 class="text-sm font-medium text-n-slate-12">
              {{ t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.HEADER') }}
            </h5>
            <p class="text-xs text-n-slate-11">
              {{
                t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.DESCRIPTION')
              }}
            </p>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{
                t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.PROVIDER.LABEL')
              }}
              <select
                v-model="provider"
                class="h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 mb-0"
              >
                <option
                  v-for="option in PROVIDERS"
                  :key="option"
                  :value="option"
                >
                  {{
                    t(
                      `INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.PROVIDER.OPTIONS.${option.toUpperCase()}`
                    )
                  }}
                </option>
              </select>
              <span class="text-xs text-n-slate-11 font-normal">
                {{
                  t(
                    'INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.PROVIDER.HELP'
                  )
                }}
              </span>
            </label>
            <label class="flex flex-col gap-1 text-sm text-n-slate-12">
              {{
                t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.MODEL.LABEL')
              }}
              <input
                v-model="model"
                type="text"
                list="conversation-parser-model-suggestions"
                :placeholder="
                  t(
                    'INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.MODEL.PLACEHOLDER'
                  )
                "
                class="h-10 rounded-lg border border-n-weak bg-n-alpha-black2 px-3 text-sm text-n-slate-12 mb-0"
              />
              <datalist id="conversation-parser-model-suggestions">
                <option
                  v-for="option in modelSuggestions"
                  :key="option"
                  :value="option"
                />
              </datalist>
              <span class="text-xs text-n-slate-11 font-normal">
                {{
                  t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.MODEL.HELP')
                }}
              </span>
            </label>
            <Input
              v-model="apiKey"
              type="password"
              autocomplete="new-password"
              :label="
                t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.API_KEY.LABEL')
              "
              :placeholder="
                t(
                  apiKeyPresent
                    ? 'INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.API_KEY.PLACEHOLDER_SAVED'
                    : 'INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.API_KEY.PLACEHOLDER'
                )
              "
              :message="
                t(
                  apiKeyPresent
                    ? 'INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.API_KEY.HELP_SAVED'
                    : 'INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.API_KEY.HELP'
                )
              "
            />
            <Input
              v-if="showApiBase"
              v-model="apiBase"
              :label="
                t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.API_BASE.LABEL')
              "
              placeholder="https://api.moonshot.ai/v1"
              :message="
                t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.LLM.API_BASE.HELP')
              "
            />
          </div>
        </div>

        <div>
          <Button
            blue
            :is-loading="isSaving"
            :label="t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.SAVE.BUTTON')"
            @click="saveSettings"
          />
        </div>
      </div>

      <div
        v-if="isConnected"
        class="flex flex-col gap-4 p-6 outline outline-n-container outline-1 bg-n-alpha-3 rounded-md shadow"
      >
        <div>
          <h4 class="text-lg font-medium text-n-slate-12">
            {{ t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.USAGE.HEADER') }}
          </h4>
          <p class="text-n-slate-11 text-sm leading-6">
            {{
              t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.USAGE.DESCRIPTION')
            }}
          </p>
        </div>
        <p v-if="!usageRows.length" class="text-n-slate-11 text-sm">
          {{ t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.USAGE.EMPTY') }}
        </p>
        <table v-else class="w-full max-w-3xl text-sm text-left">
          <thead>
            <tr class="text-n-slate-11 border-b border-n-weak">
              <th class="py-2 pr-4 font-medium">
                {{ t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.USAGE.MONTH') }}
              </th>
              <th class="py-2 pr-4 font-medium">
                {{ t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.USAGE.CALLS') }}
              </th>
              <th class="py-2 pr-4 font-medium">
                {{
                  t(
                    'INTEGRATION_SETTINGS.CONVERSATION_PARSER.USAGE.INPUT_TOKENS'
                  )
                }}
              </th>
              <th class="py-2 pr-4 font-medium">
                {{
                  t(
                    'INTEGRATION_SETTINGS.CONVERSATION_PARSER.USAGE.OUTPUT_TOKENS'
                  )
                }}
              </th>
              <th class="py-2 pr-4 font-medium">
                {{
                  t(
                    'INTEGRATION_SETTINGS.CONVERSATION_PARSER.USAGE.TOTAL_TOKENS'
                  )
                }}
              </th>
              <th class="py-2 font-medium">
                {{ t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.USAGE.MODEL') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in usageRows"
              :key="row.month"
              class="text-n-slate-12 border-b border-n-weak last:border-0"
            >
              <td class="py-2 pr-4">{{ row.month }}</td>
              <td class="py-2 pr-4">{{ row.calls }}</td>
              <td class="py-2 pr-4">{{ row.inputTokens.toLocaleString() }}</td>
              <td class="py-2 pr-4">{{ row.outputTokens.toLocaleString() }}</td>
              <td class="py-2 pr-4">{{ row.totalTokens.toLocaleString() }}</td>
              <td class="py-2">{{ row.model }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    <div v-else class="flex items-center justify-center flex-1 p-8">
      <Spinner size="" />
    </div>

    <Dialog
      ref="disconnectDialogRef"
      type="alert"
      :title="t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.DISCONNECT.TITLE')"
      :description="
        t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.DISCONNECT.MESSAGE')
      "
      :confirm-button-label="
        t('INTEGRATION_SETTINGS.CONVERSATION_PARSER.DISCONNECT.CONFIRM')
      "
      @confirm="disconnect"
    />
  </div>
</template>
